import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { RotateCcw } from 'lucide-react'
import { PageHero, PdfTile } from '@components/ui'
import { usePdfFiles } from '@hooks/useFileUploads'
import { useWorkOrders } from '@hooks/useWorkOrders'
import { useAuthStore } from '@store/useAuthStore'
import { formatStatus } from '@utils/format'
import { FileUpload } from '../types/fileUpload'
import { WorkOrder, WorkOrderStatus, isWorkOrderInWorkshop } from '../types/workOrder'
import { userService } from '@services/userService'

const getWorkOrderType = (description?: string, diagnosis?: string): 'maintenance' | 'breakdown' => {
  if (!description && !diagnosis) return 'breakdown'

  const text = `${description || ''} ${diagnosis || ''}`.toLowerCase()
  const maintenanceKeywords = [
    'revisão', 'revisao', 'manutenção', 'manutencao', 'preventiva',
    'periódica', 'periodica', 'rotina', 'check', 'inspeção', 'inspecao',
    'óleo', 'oleo', 'filtro', 'pneus', 'pneu', 'limpeza', 'calibração', 'calibracao'
  ]
  const breakdownKeywords = [
    'avaria', 'quebrado', 'partido', 'não funciona', 'nao funciona',
    'defeito', 'problema', 'ruído', 'ruido', 'barulho', 'fumo', 'fumaça', 'fumaca',
    'luz', 'aviso', 'alerta', 'pisca', 'está a', 'esta a', 'parou', 'não arranca', 'nao arranca'
  ]

  const maintenanceCount = maintenanceKeywords.filter((keyword) => text.includes(keyword)).length
  const breakdownCount = breakdownKeywords.filter((keyword) => text.includes(keyword)).length
  return maintenanceCount > breakdownCount ? 'maintenance' : 'breakdown'
}

type WorkOrderGroup = 'active' | 'finalized'

interface NormalizedWorkOrderDocument {
  id: number
  label: string
  searchable: string
  document: FileUpload
  status: WorkOrderGroup
}

const deriveStatus = (document: FileUpload): WorkOrderGroup => {
  const tags = (document.tagsArray ?? [])
    .map((tag) => tag.toLowerCase())
    .concat(document.tags ? document.tags.toLowerCase().split(',') : [])

  const statusTagRaw = tags.find((tag) => tag.startsWith('status:'))

  const statusValue =
    statusTagRaw?.replace('status:', '').trim().toLowerCase() ??
    tags.find((tag) =>
      ['finalizado', 'concluído', 'concluida', 'fechado', 'invoiced', 'completed', 'encerrado'].some((keyword) =>
        tag.includes(keyword)
      )
    )?.toLowerCase()

  if (statusValue) {
    const normalized = statusValue
      .normalize('NFD')
      .replaceAll(/[\u0300-\u036f]/g, '')
      .replaceAll(/[^a-z]/g, '')

    if (
      ['completed', 'finalizado', 'finalizada', 'concluido', 'concluida', 'invoiced', 'fechado', 'encerrado'].includes(
        normalized
      )
    ) {
      return 'finalized'
    }
  }

  const activeIndicators = ['em andamento', 'in progress', 'pendente', 'aberta', 'ativa', 'active']
  if (tags.some((tag) => activeIndicators.some((keyword) => tag.includes(keyword)))) {
    return 'active'
  }

  return 'active'
}

const isWorkOrderDocument = (document: FileUpload) => {
  const entityType = document.entityType?.toLowerCase()
  if (entityType?.includes('work') && entityType?.includes('order')) {
    return true
  }

  const tags = (document.tagsArray ?? []).map((tag) => tag.toLowerCase())
  return tags.some((tag) => tag.includes('folha') || tag.includes('obra') || tag.includes('ordem'))
}

/** entityId from API/PDF may be string; work order map keys are numbers. */
const resolveWorkOrderId = (entityId: number | string | undefined | null): number | undefined => {
  if (entityId == null || entityId === '') return undefined
  const id = Number(entityId)
  return Number.isFinite(id) ? id : undefined
}

const getWorkOrderFromMap = (
  workOrdersMap: Map<number, WorkOrder>,
  entityId: number | string | undefined | null
): WorkOrder | undefined => {
  const id = resolveWorkOrderId(entityId)
  return id != null ? workOrdersMap.get(id) : undefined
}

const getTypeTextClass = (type: 'maintenance' | 'breakdown'): string =>
  type === 'maintenance' ? 'text-blue-700' : 'text-red-700'

const getTypeBgClass = (type: 'maintenance' | 'breakdown'): string =>
  type === 'maintenance' ? 'border-blue-200 bg-blue-50' : 'border-red-200 bg-red-50'

const getStatusTextClass = (status: WorkOrderStatus): string => {
  switch (status) {
    case WorkOrderStatus.WAITING_APPROVAL:
      return 'text-primary-700'
    case WorkOrderStatus.WAITING_PARTS:
      return 'text-yellow-700'
    case WorkOrderStatus.RESOLVED:
      return 'text-cyan-700'
    case WorkOrderStatus.IN_PROGRESS:
      return 'text-blue-700'
    case WorkOrderStatus.PENDING:
      return 'text-amber-700'
    case WorkOrderStatus.SCHEDULED:
      return 'text-violet-700'
    case WorkOrderStatus.COMPLETED:
      return 'text-emerald-700'
    default:
      return 'text-secondary-600'
  }
}

const getStatusBgClass = (status: WorkOrderStatus): string => {
  switch (status) {
    case WorkOrderStatus.WAITING_APPROVAL:
      return 'border-primary-200 bg-primary-50'
    case WorkOrderStatus.WAITING_PARTS:
      return 'border-yellow-200 bg-yellow-50'
    case WorkOrderStatus.RESOLVED:
      return 'border-cyan-200 bg-cyan-50'
    case WorkOrderStatus.IN_PROGRESS:
      return 'border-blue-200 bg-blue-50'
    case WorkOrderStatus.PENDING:
      return 'border-amber-200 bg-amber-50'
    case WorkOrderStatus.SCHEDULED:
      return 'border-violet-200 bg-violet-50'
    case WorkOrderStatus.COMPLETED:
      return 'border-emerald-200 bg-emerald-50'
    default:
      return 'border-secondary-200 bg-secondary-50'
  }
}

type FilterType = 'pending' | 'active' | 'all' | 'mine' | 'scheduled' | WorkOrderStatus

/** Estados em fila de trabalho (cartões por estado; o cartão "Pendentes" agrupa todos na oficina). */
const OPEN_WORK_STATUSES: WorkOrderStatus[] = [
  WorkOrderStatus.DRAFT,
  WorkOrderStatus.IN_PROGRESS,
  WorkOrderStatus.WAITING_PARTS,
  WorkOrderStatus.WAITING_APPROVAL,
]

const getEmptyStateMessage = (filter: FilterType): { title: string; subtitle: string } => {
  switch (filter) {
    case 'mine':
      return {
        title: 'Não há folhas de obra atribuídas a si',
        subtitle: 'Quando lhe forem atribuídas folhas na oficina, aparecerão aqui.',
      }
    case 'pending':
      return {
        title: 'Não há folhas de obra na oficina',
        subtitle: 'Quando existirem veículos em atendimento, aparecerão aqui.',
      }
    case 'scheduled':
      return {
        title: 'Não há veículos agendados',
        subtitle: 'As marcações futuras aparecerão aqui.',
      }
    case WorkOrderStatus.RESOLVED:
      return {
        title: 'Não há folhas resolvidas',
        subtitle: 'Folhas aguardando conclusão pela receção aparecerão aqui.',
      }
    case WorkOrderStatus.COMPLETED:
      return {
        title: 'Não há folhas concluídas',
        subtitle: 'Folhas concluídas aparecerão aqui.',
      }
    default:
      if (OPEN_WORK_STATUSES.includes(filter as WorkOrderStatus)) {
        const label = formatStatus(filter as WorkOrderStatus)
        return {
          title: `Não há folhas com estado "${label}"`,
          subtitle: 'Ajuste os filtros ou verifique outras categorias.',
        }
      }
      return {
        title: 'Não há folhas de obra',
        subtitle: 'Quando existirem folhas de obra, aparecerão aqui.',
      }
  }
}

export function WorkOrdersPage() {
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<FilterType>('pending') // Default: Pendentes (na oficina)
  const [showOnlyMine, setShowOnlyMine] = useState(false) // Filtro "Minhas" - para todos os utilizadores
  const [showCreateWorkOrderPrompt, setShowCreateWorkOrderPrompt] = useState(false)
  const [users, setUsers] = useState<Array<{ id: number; name: string }>>([])
  const { user, role, getRole } = useAuthStore()
  const navigate = useNavigate()
  const effectiveRole = role ?? (typeof getRole === 'function' ? getRole() : null)
  const canCreateWorkOrderViaReception =
    effectiveRole === 'receptionist' || effectiveRole === 'administrator'
  const { data: documents = [], isLoading: isLoadingPdfs } = usePdfFiles({ entityType: 'work_order' })

  // Buscar usuários da oficina
  useEffect(() => {
    if (user?.workshopCompanyId) {
      userService.getActiveByWorkshop(user.workshopCompanyId)
        .then(setUsers)
        .catch(console.error)
    }
  }, [user?.workshopCompanyId])

  // Criar mapa de ID para nome de usuário
  const userNameMap = useMemo(() => {
    const map = new Map<number, string>()
    users.forEach((u) => {
      map.set(u.id, u.name)
    })
    return map
  }, [users])

  // Buscar folhas de obra da API
  const {
    data: workOrdersFromApi = [],
    isLoading: isLoadingWorkOrders,
    error: workOrdersError,
    refetch: refetchWorkOrders,
  } = useWorkOrders({
    workshopCompanyId: user?.workshopCompanyId,
    page: 0,
    size: 100,
  })
  
  // Debug: log para verificar se está buscando
  useEffect(() => {
    console.log('WorkOrdersPage - Debug:', {
      user: user,
      workshopCompanyId: user?.workshopCompanyId,
      workOrdersFromApi: workOrdersFromApi,
      isLoadingWorkOrders: isLoadingWorkOrders,
      workOrdersError: workOrdersError,
      workOrdersCount: workOrdersFromApi?.length || 0
    })
  }, [user, workOrdersFromApi, isLoadingWorkOrders, workOrdersError])
  
  const isLoading = isLoadingPdfs || isLoadingWorkOrders

  const handleReloadList = async () => {
    const result = await refetchWorkOrders()
    if (result.isError) {
      return
    }
    const orders = result.data ?? []
    if (orders.length === 0 && canCreateWorkOrderViaReception) {
      setShowCreateWorkOrderPrompt(true)
    }
  }

  const handleConfirmCreateWorkOrder = () => {
    setShowCreateWorkOrderPrompt(false)
    navigate('/homepage')
  }

  // Converter folhas de obra da API para o formato esperado
  const workOrdersAsDocuments = useMemo(() => {
    // Criar mapa de PDFs por entityId para lookup rápido
    const pdfsByEntityId = new Map<number, FileUpload>()
    documents.forEach((doc) => {
      if (doc.entityType === 'work_order' && doc.entityId != null) {
        const entityId = resolveWorkOrderId(doc.entityId)
        if (entityId != null) {
          pdfsByEntityId.set(entityId, doc)
        }
      }
    })

    return workOrdersFromApi.map((wo: WorkOrder) => {
      const isFinalized =
        wo.status === 'COMPLETED' || wo.status === 'INVOICED' || wo.status === 'CANCELLED'
      // Remover aspas do workOrderNumber se existirem
      const workOrderNumber = wo.workOrderNumber?.replace(/^["']|["']$/g, '') || ''
      const label = workOrderNumber || `Folha ${wo.id}`
      
      const searchable = [
        wo.workOrderNumber,
        wo.description,
        wo.diagnosis,
        wo.customerCompanyId?.toString(),
        wo.vehicleId?.toString(),
        wo.status,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()

      // Buscar PDF associado se existir — garantir entityType/entityId para filtros da lista
      const associatedPdf = pdfsByEntityId.get(wo.id)

      return {
        id: wo.id,
        label,
        searchable,
        document: {
          ...(associatedPdf ?? {
            id: wo.id,
            originalFilename: `${wo.workOrderNumber || `Folha-${wo.id}`}.pdf`,
            description: wo.description || wo.workOrderNumber,
            tags: `status:${wo.status}`,
            tagsArray: [`status:${wo.status}`],
            viewUrl: undefined,
            downloadUrl: undefined,
          }),
          entityType: 'work_order',
          entityId: wo.id,
        } as FileUpload,
        status: isFinalized ? 'finalized' : 'active',
      } as NormalizedWorkOrderDocument
    })
  }, [workOrdersFromApi, documents])

  const orderedDocuments = useMemo(
    () => {
      const pdfDocuments = documents
        .filter(isWorkOrderDocument)
        .map((document, index) => {
          // Remover aspas se existirem
          const cleanDescription = document.description?.trim().replace(/^["']|["']$/g, '') || ''
          const cleanFilename = document.originalFilename.replace(/\.[^/.]+$/, '').replace(/^["']|["']$/g, '')
          const label = cleanDescription || cleanFilename || `Folha ${index + 1}`

          const searchable = [
            document.originalFilename,
            document.description,
            document.tags,
            document.tagsArray?.join(' '),
            document.entityId?.toString(),
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()

          return {
            id: document.id,
            label,
            searchable,
            document,
            status: deriveStatus(document),
          } as NormalizedWorkOrderDocument
        })
      
      // Combinar PDFs com folhas de obra da API, evitando duplicatas
      const apiWorkOrdersMap = new Map(workOrdersAsDocuments.map(wo => [wo.id, wo]))
      const uniquePdfs = pdfDocuments.filter(pdf => {
        // Se o PDF tem entityId e corresponde a uma folha de obra da API, não incluir (evitar duplicata)
        // porque a work order já tem o PDF associado em workOrdersAsDocuments
        if (pdf.document.entityId && apiWorkOrdersMap.has(resolveWorkOrderId(pdf.document.entityId) ?? -1)) {
          return false
        }
        return true
      })
      
      // Combinar: work orders da API (com ou sem PDF) + PDFs órfãos (sem work order correspondente)
      // Ordenar por ID (mais recentes primeiro)
      return [...workOrdersAsDocuments, ...uniquePdfs].sort((a, b) => b.id - a.id)
    },
    [documents, workOrdersAsDocuments]
  )

  // Criar um mapa de work orders por ID para lookup rápido
  const workOrdersMap = useMemo(() => {
    return new Map(workOrdersFromApi.map((wo: WorkOrder) => [wo.id, wo]))
  }, [workOrdersFromApi])

  // Calcular contadores de status para os cards
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    const totalPending = { count: 0 }

    workOrdersFromApi.forEach((wo: WorkOrder) => {
      const statusKey = wo.status as string
      counts[statusKey] = (counts[statusKey] || 0) + 1
      if (isWorkOrderInWorkshop(wo.status)) {
        totalPending.count++
      }
    })
    
    return { ...counts, totalPending: totalPending.count } as Record<string, number> & { totalPending: number }
  }, [workOrdersFromApi])

  // Calcular contadores para "Minhas" (para todos os utilizadores)
  // Filtra por folhas atribuídas ao utilizador em estados em aberto
  const myWorkOrders = useMemo(
    () => {
      if (!user?.id) return []
      const filtered = workOrdersFromApi.filter((wo: WorkOrder) =>
        wo.assignedMechanicId === user.id && isWorkOrderInWorkshop(wo.status)
      )
      console.log('My Work Orders (pendentes):', { userId: user.id, count: filtered.length, workOrders: filtered })
      return filtered
    },
    [workOrdersFromApi, user?.id]
  )

  const myWorkOrdersCount = myWorkOrders.length
  
  // Debug: verificar se o card deve aparecer
  useEffect(() => {
    console.log('🔍 WorkOrdersPage - Filtro "Minhas" Debug:', {
      myWorkOrdersCount,
      userId: user?.id,
      userFunctionId: user?.functionId,
      filter,
      showOnlyMine,
      totalWorkOrders: workOrdersFromApi.length,
      myWorkOrders: myWorkOrders.length
    });
  }, [myWorkOrdersCount, user?.id, user?.functionId, filter, showOnlyMine, workOrdersFromApi.length, myWorkOrders.length]);
  useEffect(() => {
    console.log('WorkOrdersPage Debug:', {
      user: user?.id,
      myWorkOrdersCount,
      filter,
      showOnlyMine
    })
  }, [user?.id, myWorkOrdersCount, filter, showOnlyMine])

  const filteredDocuments = useMemo(() => {
    const term = search.trim().toLowerCase()
    let result = orderedDocuments
    
    // Aplicar filtro de status
    if (filter === 'mine') {
      // Filtro "Minhas" - para todos os utilizadores (apenas pendentes atribuídas ao usuário)
      if (user?.id) {
        result = result.filter((item) => {
          if (item.document.entityType === 'work_order' && item.document.entityId) {
            const workOrder = getWorkOrderFromMap(workOrdersMap, item.document.entityId)
            if (workOrder) {
              // Deve estar atribuída ao usuário E ser pendente
              return workOrder.assignedMechanicId === user.id && isWorkOrderInWorkshop(workOrder.status)
            }
          }
          return false
        })
      }
    } else if (filter === 'pending') {
      // Todas as folhas ainda na oficina
      result = result.filter((item) => {
        if (item.document.entityType === 'work_order' && item.document.entityId) {
          const workOrder = getWorkOrderFromMap(workOrdersMap, item.document.entityId)
          if (workOrder) {
            if (showOnlyMine && user?.id) {
              return isWorkOrderInWorkshop(workOrder.status) && workOrder.assignedMechanicId === user.id
            }
            return isWorkOrderInWorkshop(workOrder.status)
          }
        }
        return false
      })
    } else if (filter === 'active') {
      result = result.filter((item) => item.status !== 'finalized')
    } else if (filter === 'scheduled') {
      result = result.filter((item) => {
        if (item.document.entityType === 'work_order' && item.document.entityId) {
          const workOrder = getWorkOrderFromMap(workOrdersMap, item.document.entityId)
          return workOrder?.status === WorkOrderStatus.SCHEDULED
        }
        return false
      })
    } else if (filter === WorkOrderStatus.RESOLVED) {
      // Resolvidas: visíveis para a rececionista poder passar a Concluído
      result = result.filter((item) => {
        if (item.document.entityType === 'work_order' && item.document.entityId) {
          const workOrder = getWorkOrderFromMap(workOrdersMap, item.document.entityId)
          return workOrder?.status === WorkOrderStatus.RESOLVED
        }
        return false
      })
    } else if (filter === WorkOrderStatus.COMPLETED) {
      result = result.filter((item) => {
        if (item.document.entityType === 'work_order' && item.document.entityId) {
          const workOrder = getWorkOrderFromMap(workOrdersMap, item.document.entityId)
          return workOrder?.status === WorkOrderStatus.COMPLETED
        }
        return false
      })
    } else if (OPEN_WORK_STATUSES.includes(filter as WorkOrderStatus)) {
      // Filtrar por status específico (DRAFT, IN_PROGRESS, …)
      result = result.filter((item) => {
        if (item.document.entityType === 'work_order' && item.document.entityId) {
          const workOrder = getWorkOrderFromMap(workOrdersMap, item.document.entityId)
          if (workOrder) {
            return workOrder.status === filter
          }
        }
        return false
      })
    }
    // Se filter === 'all', não filtrar por status
    
    // Aplicar busca
    if (term) {
      result = result.filter((item) => item.searchable.includes(term))
    }
    
    return result
  }, [orderedDocuments, search, filter, workOrdersMap, showOnlyMine, user?.id])

  const placeholderActives = useMemo(
    () => Array.from({ length: 12 }, (_, index) => `Folha ${index + 1}`),
    []
  )


  /** Abre um ficheiro (view ou download) com auth para evitar 403 ao abrir em nova aba */
  const openFileWithAuth = async (doc: FileUpload) => {
    const url = doc.viewUrl || doc.downloadUrl
    if (!url) {
      alert('Este documento não possui URL de visualização disponível.')
      return
    }
    const token = localStorage.getItem('token')
    if (!token) {
      alert('Sessão expirada. Faça login novamente.')
      return
    }
    try {
      const fullUrl = url.startsWith('http') ? url : `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!response.ok) {
        if (response.status === 403) {
          alert('Sem permissão para ver este ficheiro.')
          return
        }
        throw new Error(`Erro ${response.status}`)
      }
      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)
      const w = window.open(blobUrl, '_blank', 'noopener,noreferrer')
      if (!w) {
        window.URL.revokeObjectURL(blobUrl)
        const a = document.createElement('a')
        a.href = blobUrl
        a.download = doc.originalFilename || 'documento.pdf'
        a.target = '_blank'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 500)
      } else {
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30000)
      }
    } catch (e) {
      console.error('Erro ao abrir ficheiro:', e)
      alert('Erro ao abrir o documento. Tente novamente.')
    }
  }

  return (
    <div className="space-y-8">
      <PageHero
        title="FOLHAS DE OBRA"
        searchPlaceholder="Pesquisar por número da folha ou cliente..."
        searchValue={search}
        onSearchChange={setSearch}
      />

      {/* Status Count Cards - scroll horizontal em mobile sem barra */}
      <div className="flex overflow-x-auto overflow-y-hidden scrollbar-hide gap-2 md:gap-4 flex-nowrap md:flex-wrap pb-1 -mx-1 md:mx-0 md:pb-0">
        {/* Pendentes — todas as folhas ainda na oficina */}
        <div
            role="button"
            tabIndex={0}
            onClick={() => {
              console.log('Clicked Pendentes')
              setFilter('pending')
              setShowOnlyMine(false)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                setFilter('pending')
                setShowOnlyMine(false)
              }
            }}
            className={`rounded-2xl md:rounded-3xl border px-4 py-3 md:px-6 md:py-4 transition-all cursor-pointer select-none flex items-center justify-between gap-2 md:gap-4 flex-shrink-0 ${
              filter === 'pending' && !showOnlyMine
                ? 'border-[#17253C] bg-[#17253C] text-white shadow-md'
                : 'border-secondary-200 bg-white hover:border-secondary-300 hover:shadow-sm'
            }`}
          >
            <p className={`text-[10px] md:text-xs font-semibold uppercase tracking-[0.2em] md:tracking-[0.3em] ${
              filter === 'pending' && !showOnlyMine ? 'text-white/70' : 'text-secondary-400'
            }`}>
              Pendentes
            </p>
            <p className={`text-sm md:text-lg font-bold ${
              filter === 'pending' && !showOnlyMine ? 'text-white' : 'text-secondary-900'
            }`}>
              {statusCounts.totalPending || 0}
            </p>
          </div>
        {/* Filtro "Minhas" - só mostrar se houver folhas atribuídas ao utilizador */}
        {myWorkOrdersCount > 0 && (
          <div
            role="button"
            tabIndex={0}
            onClick={() => {
              console.log('Clicked Minhas, current filter:', filter)
              setFilter('mine')
              setShowOnlyMine(true)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                setFilter('mine')
                setShowOnlyMine(true)
              }
            }}
            className={`rounded-2xl md:rounded-3xl border px-4 py-3 md:px-6 md:py-4 transition-all cursor-pointer select-none flex items-center justify-between gap-2 md:gap-4 flex-shrink-0 ${
              filter === 'mine'
                ? 'border-[#17253C] bg-[#17253C] text-white shadow-md'
                : 'border-secondary-200 bg-white hover:border-secondary-300 hover:shadow-sm'
            }`}
          >
            <p className={`text-[10px] md:text-xs font-semibold uppercase tracking-[0.2em] md:tracking-[0.3em] ${
              filter === 'mine' ? 'text-white/70' : 'text-secondary-400'
            }`}>
              Minhas
            </p>
            <p className={`text-sm md:text-lg font-bold ${
              filter === 'mine' ? 'text-white' : 'text-secondary-900'
            }`}>
              {myWorkOrdersCount}
            </p>
          </div>
        )}
        {/* Agendados - marcações com visita futura */}
        {(statusCounts.SCHEDULED || 0) > 0 && (
          <div
            role="button"
            tabIndex={0}
            onClick={() => {
              setFilter('scheduled')
              setShowOnlyMine(false)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                setFilter('scheduled')
                setShowOnlyMine(false)
              }
            }}
            className={`rounded-2xl md:rounded-3xl border px-4 py-3 md:px-6 md:py-4 transition-all cursor-pointer select-none flex items-center justify-between gap-2 md:gap-4 flex-shrink-0 ${
              filter === 'scheduled'
                ? 'border-[#17253C] bg-[#17253C] text-white shadow-md'
                : 'border-secondary-200 bg-white hover:border-secondary-300 hover:shadow-sm'
            }`}
          >
            <p
              className={`text-[10px] md:text-xs font-semibold uppercase tracking-[0.2em] md:tracking-[0.3em] ${
                filter === 'scheduled' ? 'text-white/70' : 'text-secondary-400'
              }`}
            >
              Agendados
            </p>
            <p
              className={`text-sm md:text-lg font-bold ${
                filter === 'scheduled' ? 'text-white' : 'text-secondary-900'
              }`}
            >
              {statusCounts.SCHEDULED || 0}
            </p>
          </div>
        )}
        {/* Resolvidas — carro ainda na oficina, aguarda conclusão pela receção */}
        <div
          role="button"
          tabIndex={0}
          onClick={() => {
            setFilter(WorkOrderStatus.RESOLVED)
            setShowOnlyMine(false)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              setFilter(WorkOrderStatus.RESOLVED)
              setShowOnlyMine(false)
            }
          }}
          className={`rounded-2xl md:rounded-3xl border px-4 py-3 md:px-6 md:py-4 transition-all cursor-pointer select-none flex items-center justify-between gap-2 md:gap-4 flex-shrink-0 ${
            filter === WorkOrderStatus.RESOLVED
              ? 'border-[#17253C] bg-[#17253C] text-white shadow-md'
              : 'border-secondary-200 bg-white hover:border-secondary-300 hover:shadow-sm'
          }`}
        >
          <p
            className={`text-[10px] md:text-xs font-semibold uppercase tracking-[0.2em] md:tracking-[0.3em] ${
              filter === WorkOrderStatus.RESOLVED ? 'text-white/70' : 'text-secondary-400'
            }`}
          >
            Resolvidas
          </p>
          <p
            className={`text-sm md:text-lg font-bold ${
              filter === WorkOrderStatus.RESOLVED ? 'text-white' : 'text-secondary-900'
            }`}
          >
            {statusCounts.RESOLVED || 0}
          </p>
        </div>
        {/* Concluídas — veículo já saiu da oficina */}
        <div
          role="button"
          tabIndex={0}
          onClick={() => {
            setFilter(WorkOrderStatus.COMPLETED)
            setShowOnlyMine(false)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              setFilter(WorkOrderStatus.COMPLETED)
              setShowOnlyMine(false)
            }
          }}
          className={`rounded-2xl md:rounded-3xl border px-4 py-3 md:px-6 md:py-4 transition-all cursor-pointer select-none flex items-center justify-between gap-2 md:gap-4 flex-shrink-0 ${
            filter === WorkOrderStatus.COMPLETED
              ? 'border-[#17253C] bg-[#17253C] text-white shadow-md'
              : 'border-secondary-200 bg-white hover:border-secondary-300 hover:shadow-sm'
          }`}
        >
          <p
            className={`text-[10px] md:text-xs font-semibold uppercase tracking-[0.2em] md:tracking-[0.3em] ${
              filter === WorkOrderStatus.COMPLETED ? 'text-white/70' : 'text-secondary-400'
            }`}
          >
            Concluídas
          </p>
          <p
            className={`text-sm md:text-lg font-bold ${
              filter === WorkOrderStatus.COMPLETED ? 'text-white' : 'text-secondary-900'
            }`}
          >
            {statusCounts.COMPLETED || 0}
          </p>
        </div>
        {/* Status específicos - só mostrar se houver folhas com esse status */}
        {OPEN_WORK_STATUSES.filter((status) => (statusCounts[status as string] || 0) > 0).map((status) => (
          <div
            key={status}
            role="button"
            tabIndex={0}
            onClick={() => {
              console.log('Clicked status:', status)
              setFilter(status)
              setShowOnlyMine(false)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                setFilter(status)
                setShowOnlyMine(false)
              }
            }}
            className={`rounded-2xl md:rounded-3xl border px-4 py-3 md:px-6 md:py-4 transition-all cursor-pointer select-none flex items-center justify-between gap-2 md:gap-4 flex-shrink-0 ${
              filter === status
                ? 'border-[#17253C] bg-[#17253C] text-white shadow-md'
                : 'border-secondary-200 bg-white hover:border-secondary-300 hover:shadow-sm'
            }`}
          >
            <p className={`text-[10px] md:text-xs font-semibold uppercase tracking-[0.2em] md:tracking-[0.3em] ${
              filter === status ? 'text-white/70' : 'text-secondary-400'
            }`}>
              {formatStatus(status)}
            </p>
            <p className={`text-sm md:text-lg font-bold ${
              filter === status ? 'text-white' : 'text-secondary-900'
            }`}>
              {statusCounts[status as string] || 0}
            </p>
          </div>
        ))}
      </div>

      <section className="space-y-4">
        <h2 className="text-[10px] md:text-sm font-semibold uppercase tracking-[0.2em] md:tracking-[0.3em] text-secondary-500">
          {filter === 'mine'
            ? 'Minhas folhas de obra'
            : filter === 'pending'
            ? 'Folhas na oficina'
            : filter === 'scheduled'
            ? 'Veículos agendados'
            : filter === WorkOrderStatus.RESOLVED
            ? 'Folhas resolvidas (aguardam conclusão)'
            : filter === WorkOrderStatus.COMPLETED
            ? 'Folhas concluídas'
            : filter === 'active'
            ? 'Folhas de obra ativas'
            : 'Todas as folhas de obra'}
        </h2>
        {workOrdersError ? (
          <div className="rounded-3xl border border-red-200 bg-red-50 p-8 text-red-700">
            <p className="font-semibold">Não foi possível carregar as folhas de obra.</p>
            <p className="mt-1 text-sm">Tente novamente ou use o botão abaixo para recarregar.</p>
            <button
              type="button"
              onClick={() => void handleReloadList()}
              className="mt-4 inline-flex items-center justify-center rounded-full border border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-100"
            >
              <RotateCcw className="h-4 w-4 shrink-0" />
              <span className="ml-2">Recarregar lista</span>
            </button>
          </div>
        ) : !isLoading && filteredDocuments.length === 0 ? (
          <div className="rounded-3xl border border-secondary-200 bg-white p-10 text-center">
            <p className="text-lg font-semibold text-secondary-700">
              {getEmptyStateMessage(filter).title}
            </p>
            <p className="mt-1 text-secondary-500">
              {getEmptyStateMessage(filter).subtitle}
            </p>
            <button
              type="button"
              onClick={() => void handleReloadList()}
              className="mt-4 inline-flex items-center justify-center rounded-full border border-secondary-200 px-5 py-2 text-sm font-semibold text-secondary-600 transition hover:border-secondary-300 hover:bg-secondary-50"
            >
              <RotateCcw className="h-4 w-4 shrink-0" />
              <span className="ml-2">Recarregar lista</span>
            </button>
          </div>
        ) : (
          <div className="grid gap-2 sm:gap-4 md:gap-5 grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 max-h-[min(22rem,58vh)] md:max-h-none overflow-y-auto md:overflow-visible scrollbar-hide">
            {isLoading
              ? placeholderActives.slice(0, 12).map((label) => (
                  <PdfTile key={`loading-${label}`} label={label} isLoading />
                ))
              : filteredDocuments.slice(0, filter === 'all' ? 18 : 12).map((item) => {
                  const normalized = item as NormalizedWorkOrderDocument

                  // Folha de obra: abrir a página de detalhe (vista do mecânico/admin), não o ficheiro
                  const isWorkOrder = normalized.document.entityType === 'work_order' && normalized.document.entityId
                  if (isWorkOrder) {
                    const workOrderId = resolveWorkOrderId(normalized.document.entityId)
                  const workOrder = workOrderId != null ? workOrdersMap.get(workOrderId) : undefined
                  const workOrderType = workOrder ? getWorkOrderType(workOrder.description, workOrder.diagnosis) : 'breakdown'
                  const workOrderDetails = workOrder
                    ? [
                        {
                          text: workOrder.assignedMechanicId
                            ? userNameMap.get(workOrder.assignedMechanicId) ?? 'Mecânico'
                            : 'Sem mecânico',
                          className: 'border-secondary-200 bg-secondary-50 text-secondary-600',
                        },
                        {
                          text: workOrderType === 'maintenance' ? 'Manutenção' : 'Avaria',
                          className: `${getTypeBgClass(workOrderType)} ${getTypeTextClass(workOrderType)}`,
                        },
                        {
                          text: formatStatus(workOrder.status),
                          className: `${getStatusBgClass(workOrder.status)} ${getStatusTextClass(workOrder.status)}`,
                        },
                      ]
                    : []

                  return (
                    <PdfTile
                      key={normalized.id}
                      label={normalized.label}
                      onClick={() => navigate(`/work-orders/${workOrderId}`)}
                      details={workOrderDetails}
                    />
                  )
                  }

                  // Documento sem URL (ex.: cliente sem PDF) – não clicável
                  const hasUrls = normalized.document.viewUrl || normalized.document.downloadUrl
                  if (!hasUrls) {
                    return (
                      <PdfTile
                        key={normalized.id}
                        label={normalized.label}
                        muted={false}
                      />
                    )
                  }

                  // Outro documento com URL (ex.: PDF de cliente): abrir ficheiro com auth
                  return (
                    <PdfTile
                      key={normalized.id}
                      label={normalized.label}
                      onClick={() => openFileWithAuth(normalized.document)}
                    />
                  )
                })}
          </div>
        )}
      </section>

      {showCreateWorkOrderPrompt && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-work-order-prompt-title"
          onClick={() => setShowCreateWorkOrderPrompt(false)}
        >
          <div
            className="mx-4 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              id="create-work-order-prompt-title"
              className="text-lg font-semibold text-secondary-900"
            >
              Não foram criadas folhas de obra. Quer criar uma folha de obra?
            </h3>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowCreateWorkOrderPrompt(false)}
                className="rounded-lg px-4 py-2 text-sm font-medium text-secondary-600 transition hover:bg-secondary-100"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmCreateWorkOrder}
                className="rounded-lg bg-[#17253C] px-4 py-2 text-sm font-medium text-white transition hover:bg-[#1e3354]"
              >
                Sim
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}
